// Compact DCA-vs-deposit pane, styled to sit in the monitoring strip alongside
// the Trend / RSI / OBV panes: a mini stock-vs-deposit curve plus a one-line
// verdict tag underneath (same shape as RuleTag). Full detail on hover.
import React, { useEffect, useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { loadExtendedMarketData } from '../../services/dataManager';
import { simulateDCA } from '../../utils/dcaSimulator';
import InfoHint from '../Common/InfoHint';

const MONTHLY_AMOUNT = 1000;
const DEPOSIT_RATE_PCT = 8;

const fmt = (v) => (v == null ? '—' : Math.round(v).toLocaleString('en-US'));

const DCAPane = ({ symbol, tooltipStyle, locked = false, onRequestLogin = () => {} }) => {
  const [candles, setCandles] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCandles(null);
    loadExtendedMarketData(symbol, '1D', { maxDataPoints: 2000 })
      .then((res) => { if (!cancelled && res?.candles?.length) setCandles(res.candles); })
      .catch(() => { /* pane stays in waiting state */ });
    return () => { cancelled = true; };
  }, [symbol]);

  const result = useMemo(
    () => (candles ? simulateDCA(candles, MONTHLY_AMOUNT, DEPOSIT_RATE_PCT) : null),
    [candles]
  );

  const s = result?.summary;
  const gapPct = s ? (s.stockValue / s.depositValue - 1) * 100 : null;
  const stockWins = s ? s.stockValue >= s.depositValue : false;

  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col h-full">
      <div className="flex items-start justify-between gap-2 mb-1 min-h-[2.5rem]">
        <span className="flex items-start gap-1"><h4 className="text-sm font-medium leading-tight text-gray-700 dark:text-gray-300">DCA vs 8% Deposit</h4><InfoHint title="DCA vs bank deposit" align="left">What if you bought a fixed amount of this stock every month (dollar-cost averaging) instead of putting the same money in an 8%/year deposit? Blue = stock DCA value, orange = deposit, dashed = total contributed. Dividend-adjusted, no fees.</InfoHint></span>
        {s && (
          <span className={`text-xs font-medium shrink-0 whitespace-nowrap ${stockWins ? 'text-green-600' : 'text-amber-600'}`}>
            {(s.months / 12).toFixed(1)}y
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={120}>
        {result ? (
          <LineChart data={result.series} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <XAxis dataKey="time" tick={false} />
            <YAxis domain={['auto', 'auto']} width={45} tick={{ fontSize: 10 }} stroke="#6B7280" tickFormatter={fmt} />
            <Tooltip formatter={(v, name) => [fmt(v), name]} contentStyle={tooltipStyle} />
            <Line type="monotone" dataKey="stock" name="Stock DCA" stroke="#3b82f6" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="deposit" name="Deposit" stroke="#f59e0b" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="invested" name="Contributed" stroke="#9ca3af" strokeWidth={1} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
          </LineChart>
        ) : (
          <div className="flex items-center justify-center h-full text-xs text-gray-400">
            {candles ? 'need ~13 months' : 'loading…'}
          </div>
        )}
      </ResponsiveContainer>
      {s && locked && (
        <div className="mt-auto pt-1 flex justify-center">
          <button
            onClick={() => onRequestLogin('DCA vs deposit backtest')}
            className="text-[11px] font-medium text-gray-400 hover:text-emerald-600 cursor-pointer text-center leading-tight border-b border-dotted border-gray-300"
            title="Log in to see the DCA-vs-deposit result"
          >
            🔒 log in for DCA result
          </button>
        </div>
      )}
      {s && !locked && (
        <div className="relative group mt-auto pt-1 flex justify-center">
          <span
            className={`text-[11px] font-medium cursor-help text-center leading-tight border-b border-dotted border-gray-300 ${
              stockWins ? 'text-green-600' : 'text-amber-600'}`}
          >
            Buy monthly · {gapPct >= 0 ? '+' : ''}{gapPct.toFixed(0)}% vs deposit
          </span>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-30 w-64 rounded-lg bg-white border border-gray-200 shadow-xl p-3 text-left">
            <div className="text-xs font-semibold text-gray-900 mb-1">DCA {MONTHLY_AMOUNT}/mo vs {DEPOSIT_RATE_PCT}% deposit</div>
            <div className="text-[11px] text-gray-600 space-y-0.5">
              <div>Invested: {fmt(s.invested)} over {(s.months / 12).toFixed(1)} years</div>
              <div>Stock DCA: <strong>{fmt(s.stockValue)}</strong> ({s.stockReturnPct >= 0 ? '+' : ''}{s.stockReturnPct.toFixed(1)}%)</div>
              <div>Deposit: {fmt(s.depositValue)} (+{s.depositReturnPct.toFixed(1)}%)</div>
              <div className={stockWins ? 'text-green-600' : 'text-amber-600'}>
                {stockWins
                  ? `Stock won by ${fmt(s.stockValue - s.depositValue)} (${gapPct.toFixed(1)}%)`
                  : `Deposit won by ${fmt(s.depositValue - s.stockValue)} (${(-gapPct).toFixed(1)}%)`}
              </div>
            </div>
            <div className="text-[10px] text-gray-400 mt-1">
              First session each month, fractional units, dividend-adjusted, no fees. Scale-invariant.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DCAPane;
